import * as React from 'react'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { History } from 'history'
import * as R from 'ramda'

import { BACKEND_URL } from '../../constants'
import { CaseInput } from './projectQueries'
import { ProjectParametersResponse } from './projectParameterQueries'

import {
  CaseDetails,
  DetailTools,
  ButtonPrimary,
  ButtonNormal,
  DetailRow,
  ErrorMsg,
  FieldValue,
  FieldHeader,
  Field
} from './Styles'
import ProjectFormHelper from './ProjectFormHelper'
import { checkParams, FormDataParams } from '.'
import { recordEvent } from '../../analytics'
import { ProjectCaseType } from '../../actions/project'

export type CaseFormData = {
  id: string | null
  name: string
  description: string
  lastReport: string | null // URL of the last generated report, if available
  lastResults: string | null // ID(?) of the last result set
} & FormDataParams

type State = {
  formData: CaseFormData
  // This is not editable but because we need to actually parse this, we store this here
  // to avoid parsing in render. This could be a class property as well.
  fireResistance: string
  editMode: boolean
  busy: boolean
  error: string | null
}

const formDataDef: CaseFormData = {
  id: null,
  name: '',
  lastReport: null,
  lastResults: null,
  beamMaterial: '',
  beamConcreteGrade: '',
  beamExtra: '',
  execution: '',
  finishing: '',
  description: '',
  spaceType: '',
  // Unused, just here to satisfy the FormDataParams type
  fireResistance: ''
}

const fieldNames = {
  name: 'Case',
  materials: 'Materials',
  beamMaterial: 'Materials',
  beamConcreteGrade: '',
  beamExtra: '',
  execution: 'Execution class',
  finishing: 'Finishing',
  description: 'Description',
  spaceType: 'Space type'
}

type InputProps = {
  projectCase: ProjectCaseType
  history: History
  disabled?: boolean

  lockTabs?: (lock: boolean) => void
  saveCase: (
    kase: CaseInput,
    spaceType: string,
    parameters: string
  ) => Promise<void>
  getReportToken: () => Promise<any> // tslint:disable-line no-any
} & RouteComponentProps<{ projectId: string }> & {
    data: ProjectParametersResponse
  }

export class ProjectCase extends React.Component<InputProps, State> {
  MyForm: ProjectFormHelper

  constructor(props: InputProps) {
    super(props)
    this.state = {
      formData: this._getFormData(props),
      fireResistance: this._parseFireResistance(props.projectCase),
      editMode: false,
      busy: false,
      error: null
    }
    this.MyForm = new ProjectFormHelper({
      setState: (changed: object) => {
        const newFormData: object = {
          formData: {
            ...this.state.formData,
            ...changed
          }
        }
        this.setState(newFormData)
      },
      editMode: false,
      fieldNames
    })
  }

  // Parse the current fire resistance value from first beam so we can display it
  _parseFireResistance(kase: ProjectCaseType): string {
    try {
      const data = JSON.parse(kase.data)
      return data.beams[0].fireResistance || '<Invalid fire resistance>'
    } catch (e) {
      return '<Invalid fire resistance>'
    }
  }

  _getFormData(props: InputProps): CaseFormData {
    const { projectCase } = props
    if (projectCase) {
      const {
        id,
        name,
        description,
        parameters,
        spaceType,
        lastResults,
        lastReport
      } = projectCase
      const params = {
        beamMaterial: '',
        beamConcreteGrade: '',
        beamExtra: '',
        execution: '',
        finishing: '',
        ...JSON.parse(parameters)
      }
      return {
        id,
        name,
        lastResults,
        lastReport,
        ...params,
        description,
        spaceType
      }
    }
    return formDataDef
  }

  componentWillReceiveProps(nextProps: InputProps) {
    const { projectCase } = nextProps
    const prevId = this.props.projectCase ? this.props.projectCase.id : null
    if (projectCase && projectCase.id !== prevId) {
      this.setState({
        formData: this._getFormData(nextProps),
        error: null,
        fireResistance: this._parseFireResistance(nextProps.projectCase)
      })
    } else {
      if (this.state.error) {
        // Hm?
        this.setState({ error: null })
      }
    }
  }

  onSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    const { formData } = this.state

    this.setState({ busy: true }, () => {
      const {
        beamMaterial,
        beamConcreteGrade,
        beamExtra,
        execution,
        finishing,
        id,
        name,
        description,
        spaceType
      } = formData
      const parameters = {
        beamMaterial,
        beamConcreteGrade,
        beamExtra,
        execution,
        finishing
      }
      const projectCase: CaseInput = { id: id || '', name, description }

      this.props
        .saveCase(projectCase, spaceType, JSON.stringify(parameters))
        .then(() => {
          this.setState({ busy: false, error: null }, () => {
            this.toggleEditMode(false)
          })
        })
        .catch(err => {
          console.warn('Save failed', err)
          this.setState({ busy: false, error: 'Save failed' })
        })
    })
    e.preventDefault()
  }

  toggleEditMode(editMode: boolean, reset?: boolean) {
    const lockTabs = this.props.lockTabs || (() => undefined)
    this.MyForm.toggleEditMode(editMode)
    if (reset) {
      this.setState(
        { editMode, error: null, formData: this._getFormData(this.props) },
        () => lockTabs(editMode)
      )
    } else {
      this.setState({ editMode }, () => lockTabs(editMode))
    }
  }

  getReport = (reportUrl: string | null) => {
    if (!reportUrl) {
      return
    }
    let win = window.open(undefined, '_blank')
    this.props
      .getReportToken()
      .then(({ data }) => {
        recordEvent('project', 'past-report-loaded')
        const accessToken = data.getReportAccessToken || ''
        if (win) {
          win.location.href = `${BACKEND_URL}/${reportUrl}?a=${accessToken}`
        }
      })
      .catch(err => {
        console.warn('Get access token failed', err)
        this.setState({ busy: false, error: 'Can not access report' })
      })
  }

  render() {
    const { formData, editMode, busy, error } = this.state
    const {
      id,
      name,
      description,
      lastReport,
      lastResults,
      beamMaterial,
      beamConcreteGrade,
      beamExtra,
      execution,
      finishing,
      spaceType
    } = formData
    const { history, disabled, data } = this.props
    const MyForm = this.MyForm

    if (!formData) {
      return null
    }

    const paramsOk = checkParams(formData, data, true) && name.length > 0
    const materials = [
      (
        R.find(p => p.id === beamMaterial, data.beamMaterials) || {
          displayName: '-unknown-'
        }
      ).displayName,
      (
        R.find(p => p.id === beamConcreteGrade, data.beamConcreteGrades) || {
          displayName: '-unknown-'
        }
      ).displayName,
      (
        R.find(p => p.id === beamExtra, data.beamExtras) || {
          displayName: '-unknown-'
        }
      ).displayName
    ].join(', ')

    return (
      <CaseDetails>
        {busy && <div className="spinner" />}
        {error && <ErrorMsg>{error}</ErrorMsg>}
        <form onSubmit={this.onSubmit} ref={'case' + id}>
          <DetailRow>
            {MyForm.renderEditField('name', name, '10em', true, true)}
            {editMode ? (
              <DetailTools>
                <ButtonPrimary
                  type={'button'}
                  onClick={() => this.toggleEditMode(false, true)}
                >
                  CANCEL
                </ButtonPrimary>
                <ButtonNormal
                  className="saveButton"
                  type={'submit'}
                  disabled={!paramsOk}
                  title={
                    paramsOk ? 'Save changes' : 'Invalid or missing fields'
                  }
                >
                  SAVE
                </ButtonNormal>
              </DetailTools>
            ) : (
              <DetailTools>
                <ButtonPrimary
                  className="editButton"
                  type={'button'}
                  onClick={() => this.toggleEditMode(true)}
                  disabled={disabled}
                >
                  EDIT
                </ButtonPrimary>

                <ButtonNormal
                  type={'button'}
                  disabled={!lastReport}
                  onClick={() => this.getReport(lastReport)}
                >
                  OPEN REPORT
                </ButtonNormal>
                <ButtonNormal
                  type={'button'}
                  disabled={!lastResults}
                  onClick={() => {
                    recordEvent('project', 'past-results-loaded')
                    history.push(
                      `/project/${
                        this.props.match.params.projectId
                      }/case/${id}/results/${lastResults}`
                    )
                  }}
                >
                  OPEN RESULTS
                </ButtonNormal>
              </DetailTools>
            )}
          </DetailRow>
          <DetailRow>
            {MyForm.renderSelectField(
              'spaceType',
              spaceType,
              data.spaceTypes,
              '10em',
              true
            )}
            {editMode &&
              MyForm.renderSelectField(
                'beamMaterial',
                beamMaterial,
                data.beamMaterials,
                '10em',
                true
              )}
            {editMode &&
              MyForm.renderSelectField(
                'beamConcreteGrade',
                beamConcreteGrade,
                data.beamConcreteGrades,
                '10em',
                true
              )}
            {editMode &&
              MyForm.renderSelectField(
                'beamExtra',
                beamExtra,
                data.beamExtras,
                '10em',
                true
              )}
            {!editMode &&
              MyForm.renderTextField('materials', materials, '32em')}
          </DetailRow>
          <DetailRow>
            <Field style={{ width: '10em' }}>
              <FieldHeader style={{ width: '10em' }}>
                Fire resistance
              </FieldHeader>
              <FieldValue style={{ width: '10em' }}>
                {this.state.fireResistance}
              </FieldValue>
            </Field>
            {MyForm.renderSelectField(
              'execution',
              execution,
              data.executionTypes,
              '10em',
              true
            )}
            {MyForm.renderSelectField(
              'finishing',
              finishing,
              data.finishingTypes,
              '10em',
              true
            )}
          </DetailRow>
          <DetailRow>
            {MyForm.renderEditTextArea('description', description, null, false)}
          </DetailRow>
        </form>
      </CaseDetails>
    )
  }
}

export default withRouter(ProjectCase)



// WEBPACK FOOTER //
// ./src/Components/Projects/ProjectCase.tsx